import { z } from 'zod';

import {
    SESSION_LOCATION_TYPES,
    sessionPhoneNumberSchema,
    sessionTelehealthLinkSchema,
} from '@nesso/session-contracts/session.models';

import { buildLocalInstant } from '@/shared/lib/time-utils';

import { parseTimeOfDay } from '../lib/parse-time-of-day';

// Restated rather than imported from `@/entities/client`, matching the sheet store's own
// restatement in `session-sheets-store.ts`.
export type ScheduleSessionClient = { id: string; name: string };

// Returns the field's real instant on `date`, or null once an issue has already been added for
// it — a missing, unparseable or nonexistent (spring-forward gap) time all refuse the same way.
function addTimeIssues(
    ctx: z.RefinementCtx,
    field: 'startTime' | 'endTime',
    value: string,
    emptyMessage: string,
    date: string,
): Date | null {
    if (value.trim() === '') {
        ctx.addIssue({ code: 'custom', message: emptyMessage, path: [field] });
        return null;
    }

    const minutes = parseTimeOfDay(value);
    if (minutes === null) {
        ctx.addIssue({ code: 'custom', message: 'Enter a valid time', path: [field] });
        return null;
    }

    const instant = buildLocalInstant(date, minutes);
    if (!instant.existsOnDate) {
        ctx.addIssue({
            code: 'custom',
            message: "This time doesn't exist on this date",
            path: [field],
        });
        return null;
    }

    return instant.date;
}

// Every text field stays a string ('' for empty); `undefined` would restore react-hook-form's
// `defaultValues` instead of refusing the submit. Only the selected location type's detail is
// validated — the other two stay untouched while a type is deselected.
export const scheduleSessionFormSchema = z
    .object({
        client: z.object({ id: z.string(), name: z.string() }).nullable(),
        date: z.string(),
        startTime: z.string(),
        endTime: z.string(),
        locationType: z.enum(SESSION_LOCATION_TYPES),
        practiceLocationId: z.string(),
        telehealthLink: z.string(),
        phoneNumber: z.string(),
        serviceCode: z.string(),
        fee: z.string(),
    })
    .superRefine((values, ctx) => {
        if (!values.client) {
            ctx.addIssue({ code: 'custom', message: 'Select a client', path: ['client'] });
        }

        const start = addTimeIssues(
            ctx,
            'startTime',
            values.startTime,
            'Enter a start time',
            values.date,
        );
        const end = addTimeIssues(ctx, 'endTime', values.endTime, 'Enter an end time', values.date);

        if (start && end && end.getTime() <= start.getTime()) {
            ctx.addIssue({
                code: 'custom',
                message: 'End time must be after start time',
                path: ['endTime'],
            });
        }

        if (values.serviceCode.trim() === '') {
            ctx.addIssue({ code: 'custom', message: 'Select a service', path: ['serviceCode'] });
        }

        if (values.fee.trim() === '') {
            ctx.addIssue({ code: 'custom', message: 'Enter a fee', path: ['fee'] });
        }

        if (values.locationType === 'TELEHEALTH' && values.telehealthLink.trim() !== '') {
            const result = sessionTelehealthLinkSchema.safeParse(values.telehealthLink);
            if (!result.success) {
                ctx.addIssue({
                    code: 'custom',
                    message: result.error.issues[0]?.message ?? 'Enter a valid link',
                    path: ['telehealthLink'],
                });
            }
        }

        if (values.locationType === 'PHONE' && values.phoneNumber.trim() !== '') {
            const result = sessionPhoneNumberSchema.safeParse(values.phoneNumber);
            if (!result.success) {
                ctx.addIssue({
                    code: 'custom',
                    message: result.error.issues[0]?.message ?? 'Enter valid 10-digit phone number',
                    path: ['phoneNumber'],
                });
            }
        }
    })
    .transform((values) => ({
        ...values,
        client: values.client as ScheduleSessionClient,
        practiceLocationId:
            values.practiceLocationId.trim() === '' ? null : values.practiceLocationId,
        telehealthLink: values.telehealthLink.trim() === '' ? null : values.telehealthLink,
        phoneNumber: values.phoneNumber.trim() === '' ? null : values.phoneNumber,
    }));

export type ScheduleSessionFormValues = z.input<typeof scheduleSessionFormSchema>;
export type ScheduleSessionFormOutput = z.output<typeof scheduleSessionFormSchema>;
